import { GoogleOutlined } from "@ant-design/icons";
import {
  CloudflareIconUrl,
  DuoIconUrl,
  EntraIdIconUrl,
  OktaIconUrl,
  PingIconUrl,
} from "@p0security/shared/integrations/constants";
import { OrgData } from "@p0security/shared/types/tenant/index";
import {
  AzureOIDCProvider,
  GoogleOIDCProvider,
  LegacyOktaSsoProvider,
  MicrosoftSsoProvider,
  OidcPkceProvider,
} from "@p0security/shared/types/tenant/sso";
import { Logo } from "components/Integrations/Logo";
import {
  Auth,
  AuthProvider,
  GoogleAuthProvider,
  NextOrObserver,
  OAuthCredential,
  OAuthProvider,
  Unsubscribe,
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  getAuth,
  isSignInWithEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "providers/FirestoreProvider";
import React from "react";
import { assertNever } from "utils/assert";

import { LimitedUse } from "../Integrations/Google/LimitedUse";
import { signInWithPkce } from "./pkce";

const verifyEmailCallbackUrl = `${window.location.origin}/login/email-verified?orgSlug=[orgSlug]`;
const changePasswordCallbackUrl = `${window.location.origin}/login/change-password?orgSlug=[orgSlug]`;

const actionCodeSettingsBase = {
  url: `${window.location.origin}/login/email-callback?orgSlug=[orgSlug]`,
  handleCodeInApp: true,
};

export namespace idPlatform {
  export async function finishEmailSignIn(
    email: string,
    tenantId: string
  ): Promise<UserCredential | undefined> {
    auth.tenantId = tenantId;
    if (isSignInWithEmailLink(auth, window.location.href)) {
      return await signInWithEmailLink(auth, email, window.location.href);
    }
  }

  export async function signIn(
    tenantDetails: OrgData,
    options: {
      email?: string;
      password?: string;
      orgSlug: string;
    }
  ): Promise<OAuthCredential | UserCredential | null | void> {
    const idp = idpSelector(tenantDetails);
    auth.tenantId = tenantDetails.tenantId;
    // `signInWithPopup` below is the actual login window managed by Google Identity Platform and the configured Provider
    // See an example for OIDC: https://cloud.google.com/identity-platform/docs/web/oidc#signing_in_users_with_oauth
    if (
      idp?.flow === "pkce" &&
      tenantDetails.auth.type === "sso" &&
      tenantDetails.auth.provider.ssoProvider === "oidc-pkce"
    ) {
      const provider = idp.authProvider();
      const result = await signInWithPkce(
        auth,
        provider as OAuthProvider,
        tenantDetails.auth.provider,
        options.orgSlug
      );
      return idp.credentialFromResult(result);
    } else if (idp !== undefined) {
      const result = await signInWithPopup(auth, idp.authProvider());
      return idp.credentialFromResult(result);
    } else if (options.password !== undefined && options.email !== undefined) {
      const actionCodeSettings = { ...actionCodeSettingsBase };
      actionCodeSettings.url = actionCodeSettings.url.replace(
        "[orgSlug]",
        options.orgSlug
      );
      return signInWithEmailAndPassword(
        auth,
        options.email,
        options.password
      ).catch((error) => alert(error.message));
    } else if (options.email !== undefined) {
      const actionCodeSettings = { ...actionCodeSettingsBase };
      actionCodeSettings.url = actionCodeSettings.url.replace(
        "[orgSlug]",
        options.orgSlug
      );
      return sendSignInLinkToEmail(
        auth,
        options.email,
        actionCodeSettings
      ).catch((error) => alert(error.message));
    }
  }

  const createVerifyEmailActionCodeSettings = async ({
    orgSlug,
    tenantId,
    url,
  }: {
    orgSlug: string;
    tenantId: string;
    url: string;
  }): Promise<{
    auth: Auth;
    actionCodeSettings: { url: string; handleCodeInApp: boolean };
  }> => {
    // TODO: only email sign up is supported for now.
    const auth: Auth = getAuth();
    auth.tenantId = tenantId;
    const actionCodeSettings = {
      handleCodeInApp: true,
      url: url.replace("[orgSlug]", orgSlug),
    };
    return { auth, actionCodeSettings };
  };

  export async function signUp(
    tenantDetails: OrgData,
    options: {
      email: string;
      password: string;
      orgSlug: string;
    }
  ) {
    // TODO: only email sign up is supported for now.
    const { auth, actionCodeSettings } =
      await createVerifyEmailActionCodeSettings({
        orgSlug: options.orgSlug,
        tenantId: tenantDetails.tenantId,
        url: verifyEmailCallbackUrl,
      });
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        options.email,
        options.password
      );

      return await sendEmailVerification(
        userCredential.user,
        actionCodeSettings
      );
    } catch (error) {
      alert(error);
    }
  }

  export async function resendEmailVerification(
    tenantDetails: OrgData,
    options: {
      user: User;
      orgSlug: string;
    }
  ) {
    const { actionCodeSettings } = await createVerifyEmailActionCodeSettings({
      orgSlug: options.orgSlug,
      tenantId: tenantDetails.tenantId,
      url: verifyEmailCallbackUrl,
    });
    try {
      return await sendEmailVerification(options.user, actionCodeSettings);
    } catch (error) {
      alert(error);
    }
  }

  export async function sendPasswordReset(
    tenantDetails: OrgData,
    options: {
      email: string;
      orgSlug: string;
    }
  ) {
    const { auth, actionCodeSettings } =
      await createVerifyEmailActionCodeSettings({
        orgSlug: options.orgSlug,
        tenantId: tenantDetails.tenantId,
        url: changePasswordCallbackUrl,
      });
    try {
      return await sendPasswordResetEmail(
        auth,
        options.email,
        actionCodeSettings
      );
    } catch (error) {
      alert(error);
    }
  }

  export async function signOut() {
    return await getAuth().signOut(); // Requires initialized Firestore
  }

  export function onAuthStateChanged(
    nextOrObserver: NextOrObserver<User>
  ): Unsubscribe {
    return getAuth().onAuthStateChanged(nextOrObserver); // Requires initialized Firestore
  }

  export function element(tenantDetails: OrgData) {
    const idp = idpSelector(tenantDetails);
    return !!idp && idp.component();
  }

  export function footer(tenantDetails: OrgData) {
    const idp = idpSelector(tenantDetails);
    return idp?.footer;
  }
}

type IdentityProvider = {
  component: () => React.ReactElement;
  authProvider: () => AuthProvider;
  credentialFromResult: (result: UserCredential) => OAuthCredential | null;
  flow?: "pkce" | "popup";
  footer?: React.ReactElement;
};

const idpSelector = (tenant: OrgData): IdentityProvider | undefined => {
  if (tenant.auth?.type !== "sso") {
    return undefined;
  }

  const { ssoProvider } = tenant.auth.provider;
  return ssoProvider === undefined
    ? undefined
    : ssoProvider === "google"
      ? googleIdentityProvider()
      : ssoProvider === "microsoft"
        ? microsoftIdentityProvider(tenant.auth.provider)
        : ssoProvider === "okta"
          ? oktaIdentityProvider(tenant.auth.provider)
          : ssoProvider === "oidc-pkce"
            ? pkceIdentityProvider(tenant.auth.provider)
            : ssoProvider === "google-oidc"
              ? googleOidcIdentityProvider(tenant.auth.provider)
              : ssoProvider === "azure-oidc"
                ? azureOidcIdentityProvider(tenant.auth.provider)
                : assertNever(tenant.auth.provider);
};

const googleIdentityProvider = (): IdentityProvider => ({
  component: () => {
    return (
      <>
        <GoogleOutlined /> Sign in with Google
      </>
    );
  },
  authProvider: () => new GoogleAuthProvider(),
  credentialFromResult: (result: UserCredential) =>
    GoogleAuthProvider.credentialFromResult(result),
  footer: <LimitedUse />,
});

const googleOidcIdentityProvider = (
  provider: GoogleOIDCProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <GoogleOutlined /> Sign in with Google
      </>
    );
  },
  authProvider: () => {
    return new OAuthProvider(`oidc.${provider.ssoProvider}`);
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
  footer: <LimitedUse />,
});

const azureOidcIdentityProvider = (
  provider: AzureOIDCProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <img src={EntraIdIconUrl} width={15} alt="Entra ID" /> Sign in with
        Microsoft | Entra ID
      </>
    );
  },
  authProvider: () => {
    return new OAuthProvider(
      `oidc.${provider.ssoProvider}`
    ).setCustomParameters({
      tenant: provider.microsoftPrimaryDomain,
    });
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});

const microsoftIdentityProvider = (
  provider: MicrosoftSsoProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <img src={EntraIdIconUrl} width={15} alt="Entra ID" /> Sign in with
        Microsoft | Entra ID
      </>
    );
  },
  authProvider: () => {
    // Prevent another Microsoft tenant or a personal account from signing in to this tenant
    // If this is not set, a random live.com account can log in to any Azure AD tenant,
    // albeit, they won't see any data because of the Firestore rules that enforce a regex match
    // on the domain using the Firestore document field `o/{tenantId}.openIdDomain`.
    // Make sure to update that field and the `orgs/{tenantSlug}.microsoftPrimaryDomain` field simultaneously.
    // If this field is set, the login attempt is blocked with the error message "User account 'user@hotmail.com'
    // from identity provider 'live.com' does not exist in tenant 'XYZ'...".
    return new OAuthProvider("microsoft.com").setCustomParameters({
      tenant: provider.microsoftPrimaryDomain,
    });
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});

const pkceIdentityProvider = (provider: OidcPkceProvider): IdentityProvider => {
  const [logoUrl, title] =
    provider.providerType === "okta"
      ? [OktaIconUrl, "Okta"]
      : provider.providerType === "cloudflare"
        ? [CloudflareIconUrl, "Cloudflare"]
        : provider.providerType === "ping"
          ? [PingIconUrl, "Ping Identity"]
          : provider.providerType === "duo"
            ? [DuoIconUrl, "Duo"]
            : assertNever(provider);
  return {
    component: () => (
      <>
        <Logo logo={logoUrl} title={title} /> Sign in with {title}
      </>
    ),
    flow: "pkce",
    authProvider: () => new OAuthProvider(provider.providerId),
    credentialFromResult: OAuthProvider.credentialFromResult,
  };
};

/** @deprecated New users should use pkceIdentityProvider instead
 *
 * This is for backwards compatibility for customers that do not yet have a PKCE-requiring native OIDC app.
 */
const oktaIdentityProvider = (
  provider: LegacyOktaSsoProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <Logo logo={OktaIconUrl} title="Okta" /> Sign in with Okta
      </>
    );
  },
  authProvider: () => {
    // See https://cloud.google.com/identity-platform/docs/web/oidc?_ga=2.24136511.-468889233.1666576248#signing_in_users_with_oauth
    // The provider ID is tenant-specific
    return new OAuthProvider(provider.providerId);
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});
